/**
 * Дюймы в футы
 * ====================================
 * TODO Добавить еще один формат результата: 10 feet, 3 inches для американцев
 * TODO What is an inch? What Is a Foot? https://www.inchcalculator.com/convert/inch-to-foot/
 * TODO Some more meta: https://www.magicbricks.com/inches-to-feet-pppfa
 * TODO References:
 *  References
 *  National Institute of Standards and Technology, Checking the Net Contents of Packaged Goods, Handbook 133 - 2019 Edition, https://nvlpubs.nist.gov/nistpubs/hb/2019/NIST.HB.133-2019.pdf
 *  National Institute of Standards and Technology, U.S. Survey Foot: Revised Unit Conversion Factors, https://www.nist.gov/pml/us-surveyfoot/revised-unit-conversion-factors
 *
 * TODO Keywords:
 *    how to convert inches to feet             14,8K difficulaty 73
 *    how to write feet and inches              1,3K  difficulaty 45
 *    how many inches to square feet            1,0K  difficulaty 50
 *    how many square inches to square feet     1,0K  difficulaty 46
 *    how to calculate square feet from inches  1,0K  difficulaty 65
 */
import { Link, LoaderFunctionArgs, MetaDescriptor, MetaFunction, useLoaderData, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import invariant from "tiny-invariant";
import H2 from "~/components/H2";
import H3 from "~/components/H3";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import Author from "~/components/tool-page/Author";
import Result from "~/components/tool-page/Result";
import ToolForm from "~/components/tool-page/ToolForm";
import { makeTitle } from "~/helpers/pageTitle";
import { createArticleSchema, schemaOrg } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import LengthCalculator, { Unit } from "~/components/LengthCalculator";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";

const CATEGORY = "length";
const TKEY = "calculators.length.inches_to_feet";
const ROUTE_ENDING = "inches-to-feet";
const LAST_UPDATED = new Date("2024-11-29T00:00:00.000Z");

export const meta: MetaFunction<typeof loader> = function({ data }) {
  invariant(data);

  const meta: MetaDescriptor[] = [
    { title: makeTitle(data?.meta.title) },
    { name: "description", content: data?.meta.description },
    createArticleSchema(
      data?.meta.locale as string,
      data?.meta.title as string,
      [],
      LAST_UPDATED,
      schemaOrg.authorAlexFedorov
    )
  ];

  if (data.meta.canonical) {
    meta.push({ tagName: "link", rel: "canonical", href: data.meta.canonical });
  }

  return meta;
};


export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "pt", "tr", "it", "pl", "ar", "th"]
};

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const locale = getLocale(request.url) ?? "";
  const url = new URL(request.url);
  const number: number | null = params.number ? Number(params.number) : null;

  const t = await getTranslator(request);

  // Calculate result if number is set
  let error: string | null = null;
  let result: number | null = null;
  let notRoundedResult: number | null = null;
  let pluralizationCount = number ?? 0;

  if (number !== null) {
    if (Number.isNaN(number) || number < 0) {
      error = t(`${TKEY}.error_invalid_number`);
    } else {
      result = inchesToFeet(number, true);
      notRoundedResult = inchesToFeet(number, false);
    }

    if (number !== Math.floor(number)) {
      if (number < 2) {
        pluralizationCount = 2;
      } else {
        pluralizationCount = Math.floor(number);
      }
    }
  }

  const title = number
    ? t(`${TKEY}.title_for_number`, { count: pluralizationCount, number })
    : t(`${TKEY}.title`);

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `${TKEY}.title`, to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];

  if (number !== null) {
    breadcrumbs.push({ title, to: `${CATEGORY}/${ROUTE_ENDING}/${number}` });
  }

  let shouldIndex = false;

  if (
    // Root page
    number === null
    || (number >= 0 && number <= 120 && number % 1 === 0)
    || (number <= 500 && number % 10 === 0)
    || (number <= 1000 && number % 50 === 0)
    || (number <= 10000 && number % 1000 === 0)
  ) {
    shouldIndex = true;
  }

  const mainCalculatorURL = url.protocol + "//" + url.host + "/" + locale + "/" + CATEGORY + "/" + ROUTE_ENDING;

  return {
    number,
    result,
    notRoundedResult,
    error,
    meta: {
      locale,
      title,
      description: number !== null
        ? t(`${TKEY}.description_for_number`, { number, tv: "", result: result }) // TODO tv
        : t(`${TKEY}.description`),
      breadcrumbs,
      /**
       * Results page should have a canonical link to the main calculator page.
       * Attempt to glue links juice from results page to the main calculator page,
       * and avoid Google spam content filter
       */
      canonical:  request.url === mainCalculatorURL ? null : mainCalculatorURL,
      shouldIndex
    }
  };
};

function inchesToFeet(number: number, round = true): number {
  const result: number = number / 12;
  return round ? Math.round(result * 1000) / 1000 : result;
}

export default function Index() {
  const {
    meta,
    number,
    result,
    notRoundedResult,
    error
  } = useLoaderData<typeof loader>();

  const { t } = useTranslation("translations");
  const locale = getLocale(useLocation().pathname);

  const commonValues = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 18, 24, 30, 36, 42, 48, 50, 54, 60, 66,
    72, 78, 84, 90, 96, 102, 100, 108, 114, 120, 500, 1000, 5000, 10000, 50000, 100000, 1000000, 10000000
  ];

  const formulaSvg = <div className="bg-gray-300 rounded-md p-6 w-[200px] text-black flex justify-center">
    <svg xmlns="http://www.w3.org/2000/svg" width="84.880px" height="18.096px"
         viewBox="0 -750 4689.6 1000"
         xmlnsXlink="http://www.w3.org/1999/xlink" aria-hidden="true">
      <defs>
        <path id="MJX-12-TEX-I-1D453"
              d="M118 -162Q120 -162 124 -164T135 -167T147 -168Q160 -168 171 -155T187 -126Q197 -99 221 27T267 267T289 382V385H242Q195 385 192 387Q188 390 188 397L195 425Q197 430 203 430T250 431Q298 431 298 432Q298 434 307 482T319 540Q356 705 465 705Q502 703 526 683T550 630Q550 594 529 578T487 561Q443 561 443 603Q443 622 454 636T478 657L487 662Q471 668 457 668Q445 668 434 658T419 630Q412 601 403 552T387 469T380 433Q380 431 435 431Q480 431 487 430T498 424Q499 420 496 407T491 391Q489 386 482 386T428 385H372L349 263Q301 15 282 -47Q255 -132 212 -173Q175 -205 139 -205Q107 -205 81 -186T55 -132Q55 -95 76 -78T118 -61Q162 -61 162 -103Q162 -122 151 -136T127 -157L118 -162Z"></path>
        <path id="MJX-12-TEX-I-1D461"
              d="M26 385Q19 392 19 395Q19 399 22 411T27 425Q29 430 36 430T87 431H140L159 511Q162 522 166 540T173 566T179 586T187 603T197 615T211 624T229 626Q247 625 254 615T261 596Q261 589 252 549T232 470L222 433Q222 431 272 431H323Q330 424 330 420Q330 398 317 385H210L174 240Q135 80 135 68Q135 26 162 26Q197 26 230 60T283 144Q285 150 288 151T303 153H307Q322 153 322 145Q322 142 319 133Q314 117 301 95T267 48T216 6T155 -11Q125 -11 98 4T59 56Q57 64 57 83V101L92 241Q127 382 128 383Q128 385 77 385H26Z"></path>
        <path id="MJX-12-TEX-N-3D"
              d="M56 347Q56 360 70 367H707Q722 359 722 347Q722 336 708 328L390 327H72Q56 332 56 347ZM56 153Q56 168 72 173H708Q722 163 722 153Q722 140 707 133H70Q56 140 56 153Z"></path>
        <path id="MJX-12-TEX-I-1D456"
              d="M184 600Q184 624 203 642T247 661Q265 661 277 649T290 619Q290 596 270 577T226 557Q211 557 198 567T184 600ZM21 287Q21 295 30 318T54 369T98 420T158 442Q197 442 223 419T250 357Q250 340 236 301T196 196T154 83Q149 61 149 51Q149 26 166 26Q175 26 185 29T208 43T235 78T260 137Q263 149 265 151T282 153Q302 153 302 143Q302 135 293 112T268 61T223 11T161 -11Q129 -11 102 10T74 74Q74 91 79 106T122 220Q160 321 166 341T173 380Q173 404 156 404H154Q124 404 99 371T61 287Q60 286 59 284T58 281T56 279T53 278T49 278T41 278H27Q21 284 21 287Z"></path>
        <path id="MJX-12-TEX-I-1D45B"
              d="M21 287Q22 293 24 303T36 341T56 388T89 425T135 442Q171 442 195 424T225 390T231 369Q231 367 232 367L243 378Q304 442 382 442Q436 442 469 415T503 336T465 179T427 52Q427 26 444 26Q450 26 453 27Q482 32 505 65T540 145Q542 153 560 153Q580 153 580 145Q580 144 576 130Q568 101 554 73T508 17T439 -10Q392 -10 371 17T350 73Q350 92 386 193T423 345Q423 404 379 404H374Q288 404 229 303L222 291L189 157Q156 26 151 16Q138 -11 108 -11Q95 -11 87 -5T76 7T74 17Q74 30 112 180T152 343Q153 348 153 366Q153 405 129 405Q91 405 66 305Q60 285 60 284Q58 278 41 278H27Q21 284 21 287Z"></path>
        <path id="MJX-12-TEX-N-2F"
              d="M423 750Q432 750 438 744T444 730Q444 725 271 248T92 -240Q85 -250 75 -250Q68 -250 62 -245T56 -231Q56 -221 230 257T407 740Q411 750 423 750Z"></path>
        <path id="MJX-12-TEX-N-31"
              d="M213 578L200 573Q186 568 160 563T102 556H83V602H102Q149 604 189 617T245 641T273 663Q275 666 285 666Q294 666 302 660V361L303 61Q310 54 315 52T339 48T401 46H427V0H416Q395 3 257 3Q121 3 100 0H88V46H114Q136 46 152 46T177 47T193 50T201 52T207 57T213 61V578Z"></path>
        <path id="MJX-12-TEX-N-32"
              d="M109 429Q82 429 66 447T50 491Q50 562 103 614T235 666Q326 666 387 610T449 465Q449 422 429 383T381 315T301 241Q265 210 201 149L142 93L218 92Q375 92 385 97Q392 99 409 186V189H449V186Q448 183 436 95T421 3V0H50V19V31Q50 38 56 46T86 81Q115 113 136 137Q145 147 170 174T204 211T233 244T261 278T284 308T305 340T320 369T333 401T340 431T343 464Q343 527 309 573T212 619Q179 619 154 602T119 569T109 550Q109 549 114 549Q132 549 151 535T170 489Q170 464 154 447T109 429Z"></path>
      </defs>
      <g stroke="#000000" fill="currentColor" strokeWidth="0" transform="scale(1,-1)">
        <g data-mml-node="math">
          <g data-mml-node="mi">
            <use data-c="1D453" xlinkHref="#MJX-12-TEX-I-1D453"></use>
          </g>
          <g data-mml-node="mi" transform="translate(550,0)">
            <use data-c="1D461" xlinkHref="#MJX-12-TEX-I-1D461"></use>
          </g>
          <g data-mml-node="mo" transform="translate(1188.8,0)">
            <use data-c="3D" xlinkHref="#MJX-12-TEX-N-3D"></use>
          </g>
          <g data-mml-node="mi" transform="translate(2244.6,0)">
            <use data-c="1D456" xlinkHref="#MJX-12-TEX-I-1D456"></use>
          </g>
          <g data-mml-node="mi" transform="translate(2589.6,0)">
            <use data-c="1D45B" xlinkHref="#MJX-12-TEX-I-1D45B"></use>
          </g>
          <g data-mml-node="TeXAtom" data-mjx-texclass="ORD" transform="translate(3189.6,0)">
            <g data-mml-node="mo">
              <use data-c="2F" xlinkHref="#MJX-12-TEX-N-2F"></use>
            </g>
          </g>
          <g data-mml-node="mn" transform="translate(3689.6,0)">
            <use data-c="31" xlinkHref="#MJX-12-TEX-N-31"></use>
            <use data-c="32" xlinkHref="#MJX-12-TEX-N-32" transform="translate(500,0)"></use>
          </g>
        </g>
      </g>
    </svg>
  </div>;

  return (<>
    <CalculatorLayout>
      <ToolHeaderNew
        name={number ? t(`${TKEY}.question_n`, { count: number }) : meta.title}
        description={meta.description}
      />

      {result !== null && number !== null && !error ? (
        <Result color="positive">
          <H3 className="first-letter:capitalize">{t("answer")}:</H3>

          <div className="mt-3 mb-8 text-center">
            <strong className="mt-8 text-4xl first-letter:capitalize"> {result} {t(`${TKEY}.unit`)}</strong>
          </div>
          <div className="mt-3 mb-8 text-center">
            {t(`${TKEY}.result`, { from: number, to: result })}
          </div>
          {result !== notRoundedResult ? (
            <div className="mt-3 mb-8 text-center">
              {t(`${TKEY}.result_is_rounded`)} <br />
              {t(`${TKEY}.precise_value`, { result: notRoundedResult })}
            </div>
          ) : null}
        </Result>
      ) : null}

      <ToolForm>
        <LengthCalculator
          from={Unit.inches}
          to={Unit.feet}
          value={number ?? 1}
        />
      </ToolForm>

      {error ?
        <Result color="negative">
          <p>{error}</p>
        </Result>
        : null}
    </CalculatorLayout>

    <AdditionalContentLayout>
      <AdditionalInfo>
        {number !== null ? <>
          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_how_to_convert_n`, { count: number })}</H2>
            <p>{t(`${TKEY}.info_h_how_to_convert_n_formula`)}</p>
            <div className="flex justify-center mb-4">
              {formulaSvg}
            </div>
            <p>{t(`${TKEY}.info_h_how_to_convert_n_expl`, { count: number })}</p>
            <p className="text-center">{number} in / 12 = {result} ft</p>
          </AdditionalInfoSection>
        </> : null}


        {number !== null ? null : <>
          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_how_many_inches_in_one_foot`)}</H2>
            <p>{t(`${TKEY}.info_p_how_many_inches_in_one_foot`)}</p>
          </AdditionalInfoSection>

          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_common_numbers`)}</H2>
            <p>{t(`${TKEY}.info_p_common_numbers`)}</p>
            <table className="w-full table table-fixed text-center border-collapse border border-gray-400 rounded-md">
              <thead>
              <tr>
                <th className="font-bold px-3 py-2 border-b border-r border-gray-400">{t(`${TKEY}.inches`)}</th>
                <th className="font-bold px-3 py-2 border-b border-gray-400">{t(`${TKEY}.unit_full`)}</th>
              </tr>
              </thead>
              <tbody>
              {commonValues.map((inches) => (
                <tr key={inches} className="hover:bg-slate-200 dark:hover:bg-slate-700">
                  <td className="px-3 py-2 border-b border-r border-gray-400">{inches}</td>
                  <td className="px-3 py-2 border-b border-gray-400">{inchesToFeet(inches)}</td>
                </tr>
              ))}
              </tbody>
            </table>
          </AdditionalInfoSection>
        </>
        }
      </AdditionalInfo>

      <Author lastUpdated={LAST_UPDATED} />
    </AdditionalContentLayout>
  </>);
}


